import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { BreadcrumbView } from '../../../../../context/breadcrumb.context'
import { useAuthContext, useSignOutTo } from '../../../../../context/auth.context'
import { useApiFormOrganizationDeletion } from '../../../../../api-new/state/content'
import { useRoleOrgOrganization } from '../../../../../role/hook/role-org'
import { Card } from '../../../../../shared/card/card'
import { Form, FormSubmitBack, FormTextConfirm } from '../../../../../shared/form/form'
import { confirmMatches } from '../../../../../shared/form/form-text-confirm'
import { IconDelete } from '../../../../../shared/icon/icon'
import { isOrganizationUnavailable, serverMessage } from '../../../../../shared/error/server-message'
import { SCHEDULED_PATH, deletionDay } from '../../../../../shared/organization-deletion/organization-deletion'
import { DeletionUnavailable } from './deletion-unavailable'
import { ErrorReference } from '../../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../../shared/error/use-error-line'

// "Delete organization" (delete organization, spec §2.5, plan Task 26): the
// owner's danger zone. It follows shared/page/page-purge.js: a card that
// says what happens (from GET /organization-deletion's preflight), then the
// organization's name typed exactly -- trimmed and case-sensitive, as the
// server checks it -- then "Delete organization". On success the owner's
// own session in this organization is over: they are signed out, and
// signing in again lands on the restore page.
//
// The words about what happens are the owner-approved plain timeline (spec
// §2.6, ruling 2026-10-03), never the internal storage detail.
//
// An impersonated session reads this page too (owner ruling 2026-10-06), and
// sees what happens, but the server refuses it the schedule: it gets a note
// in place of the form (DeletionUnavailable). A super admin gets what their
// actual role in the organization allows (owner ruling 2026-10-07): a real
// owner the form, as any owner.

const membersLine = members => (members === 1 ? '1 member is' : `${members} members are`)

export const WhatHappens = ({ name, preflight }) => {
  const purgeOn = deletionDay(preflight.purgeOn)
  const domains = preflight.domains || []
  const connections = preflight.ssoConnections || []
  const others = preflight.otherOwners || []

  return (
    <React.Fragment>
      <h5 className="mt-2">What happens next</h5>
      <ul>
        <li>
          {name} is switched off right away. {membersLine(preflight.members || 0)} signed out at once and can't sign in to it or use it.
        </li>
        {domains.length > 0 && <li>Single sign-on stops for {domains.join(', ')}.</li>}
        {domains.length === 0 && connections.length > 0 && <li>Single sign-on through {connections.join(', ')} stops.</li>}
        <li>
          Any owner can restore it for {preflight.restoreDays} days, until {purgeOn}, and nothing is lost.
        </li>
        <li>
          {name} and all of its courses, content and files are permanently deleted on {purgeOn}. This can't be undone.
        </li>
        <li>Members are emailed that it's scheduled for deletion, again if it's restored, and when it's permanently deleted.</li>
        {others.length > 0 && <li>Other owners: {others.map(o => (o.email ? `${o.name} (${o.email})` : o.name)).join(', ')}.</li>}
      </ul>
      <p className="small text-body-secondary fst-italic">Any remaining backup copies are erased automatically within 6 months.</p>
    </React.Fragment>
  )
}

export const OrganizationDelete = () => {
  const role = useRoleOrgOrganization()
  const authContext = useAuthContext()
  const signOutTo = useSignOutTo()

  const name = (authContext.state && authContext.state.organizationName) || ''

  // api-new hooks return a new object on every request-state change: read
  // it through a ref, never as an effect dependency.
  const apiFormOrganizationDeletion = useApiFormOrganizationDeletion()
  const apiRef = useRef(apiFormOrganizationDeletion)
  apiRef.current = apiFormOrganizationDeletion

  const [status, setStatus] = useState(null)
  const [loadError, loadFailure, setLoadError] = useErrorLine()
  const [error, failure, setError] = useErrorLine()
  const [submitting, setSubmitting] = useState(false)

  // Stable: Form resets itself whenever its state object changes.
  const formState = useMemo(() => ({ confirmName: '' }), [])

  useEffect(() => {
    let live = true
    apiRef.current
      .get()
      .then(result => live && setStatus(result || null))
      .catch(err => live && setLoadError(serverMessage(err, "We couldn't load this organization's details. Try again in a moment."), err))
    return () => {
      live = false
    }
  }, [setLoadError])

  const handleSubmit = async values => {
    const typed = String((values && values.confirmName) || '').trim()

    // Never sent unless it is the name, exactly (the button is disabled
    // until then; Enter in the field must not get round that).
    if (submitting || !confirmMatches(typed, name, true)) return

    setError(null)
    setSubmitting(true)
    try {
      await apiRef.current.post({ confirmName: typed })
      signOutTo(SCHEDULED_PATH)
    } catch (err) {
      // A second click that lands after the first switched the organization
      // off meets the organization gate: it is scheduled.
      if (isOrganizationUnavailable(err)) {
        signOutTo(SCHEDULED_PATH)
        return
      }
      setError(serverMessage(err, "We couldn't delete this organization. Try again."), err)
      setSubmitting(false)
    }
  }

  const preflight = status && status.preflight

  return (
    <BreadcrumbView Icon={IconDelete} text="delete organization">
      <Card title="Delete organization">
        {loadError && <p className="text-danger" role="alert">{loadError}</p>}
        {loadError && <ErrorReference error={loadFailure} className="mb-3" />}

        {preflight && preflight.isPersonal &&
          <p className="py-2">
            A personal organization is deleted only with your account. To delete it, <Link to="/org/delete-account">delete your account</Link>.
          </p>}

        {preflight && !preflight.isPersonal && status.deletion &&
          <p className="py-2">
            {name} is already scheduled for deletion on {deletionDay(status.deletion.purgeAfter)}.
          </p>}

        {preflight && !preflight.isPersonal && !status.deletion && !role.accessDelete && !role.readOnly &&
          <React.Fragment>
            <p className="py-2">Only an owner can delete this organization.</p>
            {(preflight.otherOwners || []).length > 0 &&
              <p>Owners: {preflight.otherOwners.map(o => o.name || o.email).join(', ')}.</p>}
          </React.Fragment>}

        {preflight && !preflight.isPersonal && !status.deletion && role.readOnly &&
          <div className="border border-danger rounded p-3 my-2">
            <WhatHappens name={name} preflight={preflight} />
            <DeletionUnavailable readOnly={role.readOnly} className="mb-0" />
          </div>}

        {preflight && !preflight.isPersonal && !status.deletion && role.accessDelete &&
          <div className="border border-danger rounded p-3 my-2">
            <WhatHappens name={name} preflight={preflight} />
            {error && <p className="text-danger" role="alert">{error}</p>}
            {error && <ErrorReference error={failure} className="mb-3" />}
            <Form state={formState} onSubmit={handleSubmit} disabled={submitting}>
              <FormTextConfirm name="confirmName" placeholder={name} subtext={`Type ${name} to confirm`} confirmValue={name} caseSensitive required autoFocus />
              <FormSubmitBack text="Delete organization" disable={submitting} />
            </Form>
          </div>}
      </Card>
    </BreadcrumbView>
  )
}
